import type { InventoryUnit, PackageUnit } from './units.js';
import { D, type DecimalInput } from './exactDecimal.js';
import { UNIT_SYSTEM_VERSION, calculatePackaging, exactRatio, unitDefinition, type ExactRatio, type InternalBaseUnit, type SupplyPackaging } from './unitSystem.js';

export interface SupplyCostState {
  inventoryUnit?: InventoryUnit;
  unit: string;
  quantityPerPackage?: number;
  currentBaseQuantity?: number;
  currentQuantity?: number;
  configuredPackageCost?: number;
  configuredBaseUnitCost?: number;
  averageBaseUnitCost?: number;
  averageUnitCost?: number;
  averagePackageCost?: number;
  lastBaseUnitCost?: number;
  lastUnitCost?: number;
  lastPackageCost?: number;
  unitCost?: number;
  costBasis?: 'CONFIGURED' | 'WEIGHTED_AVERAGE';
  parLevel?: number;
  quantityPerPackageExact?: string;
  currentBaseQuantityExact?: string;
  currentPackageQuantityExact?: string;
  configuredPackageCostExact?: string;
  configuredBaseUnitCostExact?: string;
  averageBaseUnitCostExact?: string;
  averagePackageCostExact?: string;
  lastBaseUnitCostExact?: string;
  lastPackageCostExact?: string;
  packaging?: SupplyPackaging | null;
  unitSystemVersion?: number;
  internalBaseUnit?: InternalBaseUnit;
  normalizedQuantity?: number;
  normalizedQuantityExact?: string;
  normalizedAverageUnitCost?: number;
  normalizedAverageUnitCostExact?: string;
  normalizedAverageUnitCostRatio?: ExactRatio;
  normalizedQuantityPerPackage?: number;
  normalizedQuantityPerPackageExact?: string;
  normalizedInventoryValueExact?: string;
}

export interface SupplyUnitProjection {
  unitSystemVersion: typeof UNIT_SYSTEM_VERSION;
  internalBaseUnit: InternalBaseUnit;
  normalizedQuantity: number; normalizedQuantityExact: string;
  normalizedAverageUnitCost: number; normalizedAverageUnitCostExact: string;
  normalizedAverageUnitCostRatio: ExactRatio;
  normalizedQuantityPerPackage: number; normalizedQuantityPerPackageExact: string;
  normalizedInventoryValueExact: string;
}

export const supplyBaseQuantityExact = (item: SupplyCostState) => D(item.currentBaseQuantityExact ?? item.currentBaseQuantity ?? item.currentQuantity ?? 0).toString();
export const supplyBaseQuantity = (item: SupplyCostState) => Number(supplyBaseQuantityExact(item));
export const packageEquivalent = (item: SupplyCostState) => D(supplyBaseQuantityExact(item)).div(item.quantityPerPackageExact ?? item.quantityPerPackage ?? 1).toNumber();
export const inventoryStatusFor = (quantity: DecimalInput, parLevel: DecimalInput = 0): 'OUT' | 'LOW' | 'IN_STOCK' => D(quantity).cmp(0) <= 0 ? 'OUT' : D(quantity).cmp(parLevel) <= 0 ? 'LOW' : 'IN_STOCK';

export function configuredPackageCostExact(item: SupplyCostState): string {
  const configuredPackage = item.configuredPackageCostExact ?? item.configuredPackageCost;
  if (configuredPackage !== undefined) return D(configuredPackage).toString();
  const configuredBase = item.configuredBaseUnitCostExact ?? item.configuredBaseUnitCost;
  if (configuredBase !== undefined) return D(configuredBase).mul(item.quantityPerPackageExact ?? item.quantityPerPackage ?? 1).toString();
  const configured = item.lastPackageCostExact ?? item.lastPackageCost ?? item.averagePackageCostExact ?? item.averagePackageCost;
  return configured === undefined ? D(item.unitCost ?? item.lastBaseUnitCostExact ?? item.lastBaseUnitCost ?? item.lastUnitCost ?? item.averageBaseUnitCostExact ?? item.averageBaseUnitCost ?? item.averageUnitCost ?? 0).mul(item.quantityPerPackageExact ?? item.quantityPerPackage ?? 1).toString() : D(configured).toString();
}
export const configuredPackageCost = (item: SupplyCostState) => Number(configuredPackageCostExact(item));
export const configuredBaseUnitCostExact = (item: SupplyCostState) => D(configuredPackageCostExact(item)).div(item.quantityPerPackageExact ?? item.quantityPerPackage ?? 1).toString();
export const configuredBaseUnitCost = (item: SupplyCostState) => Number(configuredBaseUnitCostExact(item));
export const weightedBaseUnitCostExact = (item: SupplyCostState) => D(item.averageBaseUnitCostExact ?? item.averageBaseUnitCost ?? item.averageUnitCost ?? item.lastBaseUnitCostExact ?? item.lastBaseUnitCost ?? item.lastUnitCost ?? item.unitCost ?? item.configuredBaseUnitCostExact ?? item.configuredBaseUnitCost ?? configuredBaseUnitCostExact(item)).toString();
export const weightedBaseUnitCost = (item: SupplyCostState) => Number(weightedBaseUnitCostExact(item));
export const weightedPackageCost = (item: SupplyCostState) => D(weightedBaseUnitCostExact(item)).mul(item.quantityPerPackageExact ?? item.quantityPerPackage ?? 1).toNumber();

/** Additive projection, never a reinterpretation of historical principal-unit fields.
 * Return null for unsupported/incomplete legacy inputs so migration can report them.
 * Repeating normalized rates retain a numerator/denominator in addition to their 36dp view.
 */
export function supplyUnitProjection(item: SupplyCostState): SupplyUnitProjection | null {
  const definition = unitDefinition(item.inventoryUnit || item.unit);
  const quantityInput = item.currentBaseQuantityExact ?? item.currentBaseQuantity ?? item.currentQuantity;
  const packageInput = item.quantityPerPackageExact ?? item.quantityPerPackage;
  const costs = [item.averageBaseUnitCostExact, item.averageBaseUnitCost, item.averageUnitCost, item.lastBaseUnitCostExact, item.lastBaseUnitCost, item.lastUnitCost, item.unitCost,
    item.configuredBaseUnitCostExact, item.configuredBaseUnitCost, item.configuredPackageCostExact, item.configuredPackageCost, item.lastPackageCostExact, item.lastPackageCost, item.averagePackageCostExact, item.averagePackageCost];
  if (!definition || quantityInput === undefined || packageInput === undefined || !costs.some(value => value !== undefined && value !== null)) return null;
  try {
    const quantity = D(quantityInput), size = D(packageInput);
    const baseOnly = !costs.slice(0, 7).some(value => value !== undefined && value !== null) && !costs.slice(9).some(value => value !== undefined && value !== null);
    const rate = D(baseOnly ? item.configuredBaseUnitCostExact ?? item.configuredBaseUnitCost! : weightedBaseUnitCostExact(item));
    if (quantity.cmp(0) < 0 || size.cmp(0) <= 0 || rate.cmp(0) < 0) return null;
    const normalized = quantity.mul(definition.factorExact), normalizedRate = rate.div(definition.factorExact), normalizedSize = size.mul(definition.factorExact);
    return { unitSystemVersion: UNIT_SYSTEM_VERSION, internalBaseUnit: definition.internalBaseUnit,
      normalizedQuantity: normalized.toNumber(), normalizedQuantityExact: normalized.toString(),
      normalizedAverageUnitCost: normalizedRate.toNumber(), normalizedAverageUnitCostExact: normalizedRate.toString(), normalizedAverageUnitCostRatio: exactRatio(normalizedRate),
      normalizedQuantityPerPackage: normalizedSize.toNumber(), normalizedQuantityPerPackageExact: normalizedSize.toString(), normalizedInventoryValueExact: normalized.mul(normalizedRate).toString() };
  } catch { return null; }
}

/** Applies a new exact balance without rewriting historical receipts or guessing lost precision. */
export function supplyStockPatch(item: SupplyCostState, quantity: DecimalInput, options: { baseUnitCost?: DecimalInput; costBasis?: SupplyCostState['costBasis'] } = {}) {
  const balance = D(quantity);
  if (balance.cmp(0) < 0) throw Object.assign(new Error('Inventory cannot be negative.'), { statusCode: 409, code: 'INSUFFICIENT_INVENTORY' });
  if (options.baseUnitCost === undefined && (balance.cmp(0) > 0 || D(supplyBaseQuantityExact(item)).cmp(0) > 0)
    && !supplyUnitProjection({ ...item, quantityPerPackage: item.quantityPerPackage ?? 1 })) {
    throw Object.assign(new Error('Stock changes require a known valuation or an explicitly reviewed unit cost.'), { statusCode: 409, code: 'INVENTORY_COST_UNKNOWN' });
  }
  const rate = D(options.baseUnitCost ?? weightedBaseUnitCostExact(item));
  const size = D(item.quantityPerPackageExact ?? item.quantityPerPackage ?? 1);
  const packages = balance.div(size), packageRate = rate.mul(size);
  const patch = { currentBaseQuantity: balance.toNumber(), currentBaseQuantityExact: balance.toString(), currentQuantity: balance.toNumber(),
    currentPackageQuantity: packages.toNumber(), currentPackageQuantityExact: packages.toString(),
    averageBaseUnitCost: rate.toNumber(), averageBaseUnitCostExact: rate.toString(), averageUnitCost: rate.toNumber(),
    averagePackageCost: packageRate.toNumber(), averagePackageCostExact: packageRate.toString(),
    costBasis: options.costBasis ?? item.costBasis ?? 'WEIGHTED_AVERAGE', status: inventoryStatusFor(balance, item.parLevel ?? 0) };
  return { ...patch, ...supplyUnitProjection({ ...item, ...patch }) };
}

/** Weighted-average receipt math runs in g/ml/each; compatibility fields stay in the principal unit. */
export function receiveSupplyStock(item: SupplyCostState, receivedQuantityPrincipal: DecimalInput, receivedTotalCost: DecimalInput) {
  const definition = unitDefinition(item.inventoryUnit || item.unit);
  if (!definition) throw Object.assign(new Error('The inventory unit cannot be normalized.'), { statusCode: 400, code: 'INVENTORY_UNIT_INVALID' });
  const received = D(receivedQuantityPrincipal), receiptCost = D(receivedTotalCost), previousQuantity = D(supplyBaseQuantityExact(item));
  if (received.cmp(0) <= 0 || receiptCost.cmp(0) < 0 || previousQuantity.cmp(0) < 0) throw Object.assign(new Error('Invalid inventory receipt quantity or cost.'), { statusCode: 400, code: 'INVALID_NUMBER' });
  if (previousQuantity.cmp(0) > 0 && !supplyUnitProjection(item)) throw Object.assign(new Error('Existing stock needs a known valuation before a weighted-average receipt can be calculated.'), { statusCode: 409, code: 'INVENTORY_COST_UNKNOWN' });
  const previousNormalized = previousQuantity.mul(definition.factorExact), receivedNormalized = received.mul(definition.factorExact);
  const priorRate = previousQuantity.cmp(0) === 0 ? D(0) : D(weightedBaseUnitCostExact(item)).div(definition.factorExact);
  const normalizedTotal = previousNormalized.add(receivedNormalized);
  const newRateNormalized = previousNormalized.mul(priorRate).add(receiptCost).div(normalizedTotal);
  const lastRate = receiptCost.div(received), packageRate = lastRate.mul(item.quantityPerPackageExact ?? item.quantityPerPackage ?? 1);
  return { ...supplyStockPatch(item, normalizedTotal.div(definition.factorExact), { baseUnitCost: newRateNormalized.mul(definition.factorExact), costBasis: 'WEIGHTED_AVERAGE' }),
    lastBaseUnitCost: lastRate.toNumber(), lastBaseUnitCostExact: lastRate.toString(), lastUnitCost: lastRate.toNumber(), unitCost: lastRate.toNumber(),
    lastPackageCost: packageRate.toNumber(), lastPackageCostExact: packageRate.toString() };
}

export function isInventoryUnitLocked(item: SupplyCostState, hasReferences = false): boolean {
  return supplyBaseQuantity(item) !== 0 || hasReferences;
}

export interface SupplyConfigurationInput {
  purchaseUnit: PackageUnit;
  inventoryUnit: InventoryUnit;
  quantityPerPackage: number;
  packageCost: number;
  parLevel: number;
  quantityPerPackageExact?: string;
  packageCostExact?: string;
  packaging?: SupplyPackaging | null;
}

/** Changes a catalog price/pack specification, never the quantity or value already on hand. */
export function supplyConfigurationPatch(item: SupplyCostState, input: SupplyConfigurationInput, hasReferences = false) {
  const unitChanged = input.inventoryUnit !== (item.inventoryUnit || item.unit);
  if (unitChanged && isInventoryUnitLocked(item, hasReferences)) {
    throw Object.assign(new Error('The inventory unit is locked because this item has stock or linked inventory records. Use a controlled inventory adjustment before changing its unit.'), { statusCode: 409, code: 'INVENTORY_UNIT_LOCKED' });
  }
  // A new catalog quote cannot establish the cost of stock already received.
  // Keep unknown legacy valuation unresolved until a controlled opening-cost review.
  if (D(supplyBaseQuantityExact(item)).cmp(0) > 0 && !supplyUnitProjection({ ...item, quantityPerPackage: item.quantityPerPackage ?? 1 })) {
    throw Object.assign(new Error('Existing stock needs a known valuation before its catalog configuration can be changed.'), { statusCode: 409, code: 'INVENTORY_COST_UNKNOWN' });
  }
  const packaging = input.packaging === undefined ? item.packaging : input.packaging;
  const pack = packaging ? calculatePackaging(packaging, input.inventoryUnit, input.packageCostExact ?? input.packageCost) : null;
  const size = D(pack?.quantityPerPackageExact ?? input.quantityPerPackageExact ?? input.quantityPerPackage);
  const price = D(input.packageCostExact ?? input.packageCost);
  const baseCost = price.div(size);
  const currentBaseQuantity = supplyBaseQuantity(item);
  // New, unused catalog items have an estimate, not a purchased inventory valuation.
  const useConfiguredBasis = unitChanged || (item.costBasis === 'CONFIGURED' && currentBaseQuantity === 0);
  const averageBaseUnitCost = useConfiguredBasis ? baseCost : D(weightedBaseUnitCostExact(item));
  const costBasis = useConfiguredBasis ? 'CONFIGURED' as const : item.costBasis ?? 'WEIGHTED_AVERAGE' as const;
  return {
    purchaseUnit: input.purchaseUnit, inventoryUnit: input.inventoryUnit, unit: input.inventoryUnit,
    quantityPerPackage: size.toNumber(), quantityPerPackageExact: size.toString(), parLevel: input.parLevel,
    ...(packaging === undefined ? {} : { packaging: pack?.packaging ?? null }),
    configuredPackageCost: price.toNumber(), configuredPackageCostExact: price.toString(), configuredBaseUnitCost: baseCost.toNumber(), configuredBaseUnitCostExact: baseCost.toString(),
    ...supplyStockPatch({ ...item, inventoryUnit: input.inventoryUnit, unit: input.inventoryUnit, parLevel: input.parLevel, quantityPerPackage: size.toNumber(), quantityPerPackageExact: size.toString() }, supplyBaseQuantityExact(item), { baseUnitCost: averageBaseUnitCost, costBasis }),
    // No receipt history exists when changing the unit of an unused item.
    ...(unitChanged ? { unitCost: baseCost.toNumber(), lastUnitCost: baseCost.toNumber(), lastBaseUnitCost: baseCost.toNumber(), lastBaseUnitCostExact: baseCost.toString(), lastPackageCost: price.toNumber(), lastPackageCostExact: price.toString() } : {}),
  };
}
